import React, { useMemo, useState } from 'react';

import { Checkbox, Chip, FormControlLabel, Link, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { type ExperimentalFeatureKey } from '@luxuryescapes/contract-svc-promo';

import useCurrentUser from '~/hooks/useCurrentUser';
import { useUserPromoRoles } from '~/hooks/useUserPromoRoles';

import { promoRoles } from '~/services/PromoService';

import { getExperimentalPromoFeatures } from './promoAdminExperimentalFeatures';
import { type PromoFeatureIndicator, getOptInFeatureKeys, getPromoFeatureIndicators } from './promoFeatureIndicators';
import { usePromoFeatureOptIns } from './usePromoFeatureOptIns';

interface Props {
  featureUrls: Readonly<Partial<Record<ExperimentalFeatureKey, string>>>;
}

function matchesSearch(feature: PromoFeatureIndicator, searchText: string) {
  const normalizedSearchText = searchText.trim().toLowerCase();

  if (!normalizedSearchText) {
    return true;
  }

  return [
    feature.key,
    feature.displayName,
    feature.description,
    feature.featureStatusHeadline,
    feature.currentStatusHeadline,
    feature.isFunctionallyComplete ? 'functionally complete' : '',
  ].some((value) => value.toLowerCase().includes(normalizedSearchText));
}

export default function PromoExperimentalFeatures({ featureUrls }: Props) {
  const { user } = useCurrentUser();
  const { hasRole } = useUserPromoRoles({ userId: user?.id_member ?? '', enabled: Boolean(user?.id_member) });
  const [searchText, setSearchText] = useState('');
  const experimentalFeatures = useMemo(() => getExperimentalPromoFeatures(), []);
  const optInFeatureKeys = useMemo(() => getOptInFeatureKeys(experimentalFeatures), [experimentalFeatures]);
  const { optedInFeatureKeys, setOptedIn } = usePromoFeatureOptIns(user?.id_member ?? 'anonymous', optInFeatureKeys);
  const isPromoBetaTester = hasRole(promoRoles.PROMO_BETA_TESTER_ROLE);
  const featureIndicators = useMemo(() => getPromoFeatureIndicators(experimentalFeatures), [experimentalFeatures]);
  const features = useMemo(
    () => featureIndicators.filter((feature) => matchesSearch(feature, searchText)),
    [featureIndicators, searchText],
  );
  const columns = useMemo<Array<GridColDef<PromoFeatureIndicator>>>(
    () => [
      {
        field: 'displayName',
        headerName: 'Feature',
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Stack sx={{ pl: params.row.depth * 2 }}>
            <Typography variant="body2">{params.row.displayName}</Typography>
            {params.row.parentKeys.length > 0 && (
              <Typography color="text.secondary" variant="caption">
                Part of {params.row.parentKeys.join(' / ')}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 280,
      },
      {
        field: 'currentStatusHeadline',
        headerName: 'Current rollout',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'isOnlyTest',
        headerName: 'Test only',
        minWidth: 110,
        renderCell: (params) =>
          params.row.isOnlyTest ? <Chip label="Test only" size="small" color="warning" /> : null,
      },
      {
        field: 'isFunctionallyComplete',
        headerName: 'Complete',
        minWidth: 120,
        renderCell: (params) =>
          params.row.isFunctionallyComplete ? <Chip label="Complete" size="small" color="success" /> : null,
      },
      {
        field: 'optIn',
        headerName: 'This session',
        minWidth: 170,
        renderCell: (params) => {
          if (!params.row.canOptIn) {
            return null;
          }

          if (!isPromoBetaTester) {
            return <Chip label="Promo beta testers only" size="small" />;
          }

          return (
            <FormControlLabel
              control={
                <Checkbox
                  checked={optedInFeatureKeys.has(params.row.key)}
                  onChange={(event) => setOptedIn(params.row.key, event.target.checked)}
                />
              }
              label="Enabled"
            />
          );
        },
      },
      {
        field: 'openFeature',
        headerName: 'Open',
        sortable: false,
        filterable: false,
        minWidth: 120,
        renderCell: (params) => {
          const featureUrl = featureUrls[params.row.key];

          return featureUrl ? <Link href={featureUrl}>Open feature</Link> : null;
        },
      },
    ],
    [featureUrls, isPromoBetaTester, optedInFeatureKeys, setOptedIn],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Promo service features and their rollout status. Promo beta testers can enable explicitly opt-in capabilities
        for this browser session.
      </Typography>

      <TextField
        label="Search promo features"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        fullWidth
      />

      <DataGrid
        aria-label="Promo service features"
        columns={columns}
        rows={features}
        getRowId={(feature) => feature.key}
        getRowHeight={() => 'auto'}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        autoHeight
        sx={{ '.MuiDataGrid-cell': { alignItems: 'center', py: 1 } }}
      />
    </Stack>
  );
}
